// "¿Olvidaste tu contraseña?" y "Elegí tu contraseña nueva" (documento 20, decisión D51).
// - Con SMTP en el servidor, el enlace (un solo uso, 1 hora) llega al correo; la pantalla dice lo mismo exista o no la
//   cuenta. Sin SMTP: "Pedile a <marca> que te mande un enlace para crear una contraseña nueva" (la distribuidora lo
//   genera desde el panel y lo manda por WhatsApp). En la demostración (MODO_DEMO), el enlace aparece en pantalla.
// - El enlace abre /clave#t=<…>: se lee y se saca de la dirección, se muestra de quién es, se elige la contraseña
//   (8 caracteres o más) y se entra directo.
import { Icono, useToast } from '@reparto/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { CLAVE_MINIMA } from '../compartido';
import { Aviso, Boton, Cargando } from '../componentes/base';
import { Bienvenida } from '../componentes/Bienvenida';
import { CampoClave, CampoTexto } from '../componentes/Campos';
import { ErrorApi, mensajeDeError } from '../datos/api';
import {
  type DatosEnlaceClave,
  elegirClave,
  pedirEnlaceClave,
  type RespuestaOlvide,
  revisarEnlaceClave,
} from '../datos/cuenta';
import { correoLimpio, errorClaveNueva, errorCorreo, tomarTokenClave } from '../datos/entrada';
import { useMarca } from '../datos/marca';
import { tomarVolver } from '../datos/volver';
import type { EstadoEntrar } from './Entrar';
import { EnlacePrivacidad } from './Privacidad';

function VolverAEntrar({ estado }: { estado?: EstadoEntrar }) {
  return (
    <Link
      to="/entrar"
      state={estado}
      className="-mt-2 mb-4 -ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60"
    >
      <Icono nombre="chevron_left" tamano={22} />
      Volver a entrar
    </Link>
  );
}

export function Olvide() {
  const marca = useMarca((s) => s.marca);
  const { state } = useLocation() as { state: EstadoEntrar | null };
  const [email, setEmail] = useState(state?.email ?? '');
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [respuesta, setRespuesta] = useState<RespuestaOlvide | null>(null);
  const nombre = marca?.nombre ?? 'la distribuidora';
  const acceso = marca?.acceso;
  // Sin correo saliente (y sin la demostración, que muestra el enlace acá): se pide a la distribuidora.
  const soloPedir = acceso?.recuperar !== 'correo' && !acceso?.enlace_en_pantalla;
  const estado = { ...state, email: correoLimpio(email) || state?.email } satisfies EstadoEntrar;

  async function pedir(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const e = errorCorreo(email);
    setErrorEmail(e);
    if (e) return;
    setOcupado(true);
    try {
      setRespuesta(await pedirEnlaceClave(correoLimpio(email)));
    } catch (x) {
      setError(mensajeDeError(x));
    } finally {
      setOcupado(false);
    }
  }

  const pedirALaDistribuidora = (
    <Aviso
      tono="info"
      icono="chat"
      titulo={`Pedile a ${nombre} que te mande un enlace para crear una contraseña nueva`}
    >
      Escribile por donde hacés tus pedidos. Te llega un enlace por WhatsApp: lo abrís, elegís tu contraseña y entrás.
    </Aviso>
  );

  return (
    <Bienvenida marca={marca}>
      <VolverAEntrar estado={estado} />
      <h2 className="text-2xl font-bold">¿Olvidaste tu contraseña?</h2>
      {soloPedir ? (
        <div className="mt-5">{pedirALaDistribuidora}</div>
      ) : respuesta ? (
        <div className="mt-5 flex flex-col gap-4" aria-live="polite">
          {respuesta.modo === 'correo' ? (
            <Aviso tono="ok" icono="mark_email_read" titulo="Revisá tu correo">
              Si <b className="break-all">{correoLimpio(email)}</b> tiene cuenta en esta tienda, te mandamos un enlace
              para elegir una contraseña nueva. Vale 1 hora y sirve una sola vez. Si no llega, mirá en «Correo no
              deseado».
            </Aviso>
          ) : (
            pedirALaDistribuidora
          )}
          {respuesta.enlace_demostracion ? (
            <Aviso tono="aviso" icono="info" titulo="Enlace de demostración">
              Esta tienda es de prueba, por eso el enlace aparece acá. En la tienda real llega al correo.
              <a
                href={respuesta.enlace_demostracion}
                className="mt-3 flex min-h-12 items-center justify-center gap-2 rounded-full bg-primary-600 px-4 font-semibold text-sobre-marca"
              >
                <Icono nombre="key" />
                Elegir mi contraseña nueva
              </a>
            </Aviso>
          ) : acceso?.enlace_en_pantalla ? (
            <p className="text-[15px] text-stone-700">
              Si ese correo tiene cuenta en esta tienda de prueba, el enlace aparece acá. Revisá cómo lo escribiste.
            </p>
          ) : null}
          <Boton variante="secundario" bloque tamano="lg" onClick={() => setRespuesta(null)}>
            Probar con otro correo
          </Boton>
        </div>
      ) : (
        <form onSubmit={pedir} noValidate className="mt-1 flex flex-col gap-5">
          <p className="text-stone-600">
            {acceso?.recuperar === 'correo'
              ? 'Escribí el correo de tu cuenta y te mandamos un enlace para elegir una contraseña nueva.'
              : 'Escribí el correo de tu cuenta.'}
          </p>
          <CampoTexto
            etiqueta="Correo"
            icono="mail"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="nombre@gmail.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errorEmail) setErrorEmail(null);
            }}
            error={errorEmail}
          />
          {error ? (
            <Aviso tono="peligro" icono="error">
              <span role="alert">{error}</span>
            </Aviso>
          ) : null}
          <Boton type="submit" bloque tamano="lg" icono="send" cargando={ocupado} className="text-lg!">
            Mandame el enlace
          </Boton>
        </form>
      )}
      <p className="mt-8 text-center">
        <EnlacePrivacidad />
      </p>
    </Bienvenida>
  );
}

// El enlace llega siempre como una carga completa de la página: se lee apenas abre (y se saca de la dirección) y queda
// acá mientras se usa, aunque la pantalla se monte dos veces (modo estricto de React).
let tokenGuardado: string | null =
  typeof location !== 'undefined' && location.pathname === '/clave' ? tomarTokenClave() : null;

type EstadoEnlace =
  | { tipo: 'revisando' }
  | { tipo: 'listo'; token: string; datos: DatosEnlaceClave }
  | { tipo: 'invalido'; mensaje: string };

export function Clave() {
  const marca = useMarca((s) => s.marca);
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const [enlace, setEnlace] = useState<EstadoEnlace>({ tipo: 'revisando' });
  const [clave, setClave] = useState('');
  const [errorClave, setErrorClave] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    const token = tomarTokenClave() ?? tokenGuardado;
    tokenGuardado = token;
    if (!token) {
      setEnlace({ tipo: 'invalido', mensaje: 'Abrí el enlace completo que te llegó (por correo o por WhatsApp).' });
      return;
    }
    let vivo = true;
    revisarEnlaceClave(token).then(
      (datos) => vivo && setEnlace({ tipo: 'listo', token, datos }),
      (e: unknown) =>
        vivo &&
        setEnlace({
          tipo: 'invalido',
          mensaje: e instanceof ErrorApi && e.codigo === 'ENLACE_INVALIDO' ? e.message : mensajeDeError(e),
        }),
    );
    return () => {
      vivo = false;
    };
  }, []);

  if (enlace.tipo === 'revisando') return <Cargando texto="Abriendo el enlace…" />;

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    if (enlace.tipo !== 'listo') return;
    setError(null);
    const e = errorClaveNueva(clave);
    setErrorClave(e);
    if (e) return;
    setOcupado(true);
    try {
      const r = await elegirClave(enlace.token, clave);
      tokenGuardado = null;
      mostrar('Listo: ya entraste con tu contraseña nueva.', { icono: 'check_circle' });
      if (r.estado === 'falta_completar') return navegar('/registro', { replace: true });
      const v = tomarVolver();
      navegar(v?.ruta ?? '/', { replace: true, state: v ? { confirmar: v.confirmar } : undefined });
    } catch (x) {
      setOcupado(false);
      if (x instanceof ErrorApi && x.codigo === 'ENLACE_INVALIDO') setEnlace({ tipo: 'invalido', mensaje: x.message });
      else setError(mensajeDeError(x));
    }
  }

  return (
    <Bienvenida marca={marca}>
      {enlace.tipo === 'invalido' ? (
        <>
          <h2 className="text-2xl font-bold">El enlace no sirve</h2>
          <Aviso tono="peligro" icono="link_off" className="mt-5">
            {enlace.mensaje}
          </Aviso>
          <div className="mt-6 flex flex-col gap-3">
            <Link
              to="/olvide"
              className="flex min-h-14 items-center justify-center rounded-full bg-primary-600 px-5 text-lg font-semibold text-sobre-marca"
            >
              Pedir un enlace nuevo
            </Link>
            <Link
              to="/entrar"
              className="flex min-h-12 items-center justify-center rounded-full px-5 font-semibold text-primary-700 hover:bg-primary-50"
            >
              Entrar con mi contraseña
            </Link>
          </div>
        </>
      ) : (
        <form onSubmit={guardar} noValidate className="flex flex-col gap-5">
          <div>
            <h2 className="text-2xl font-bold">
              {enlace.datos.nueva ? 'Creá tu contraseña' : 'Elegí tu contraseña nueva'}
            </h2>
            <p className="mt-1 text-stone-600">
              {enlace.datos.nombre ? `${enlace.datos.nombre}, ` : ''}con ella entrás a la tienda de{' '}
              {marca?.nombre ?? 'la distribuidora'}.
            </p>
          </div>
          {enlace.datos.email ? (
            <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary-50 text-primary-700">
                <Icono nombre="mail" />
              </span>
              <div className="min-w-0">
                <p className="text-[15px] text-stone-600">Tu correo para entrar</p>
                <p className="font-semibold break-all text-stone-900">{enlace.datos.email}</p>
              </div>
            </div>
          ) : null}
          <CampoClave
            etiqueta={enlace.datos.nueva ? 'Contraseña' : 'Contraseña nueva'}
            ayuda={`${CLAVE_MINIMA} caracteres o más.`}
            autoComplete="new-password"
            autoFocus
            value={clave}
            onChange={(e) => {
              setClave(e.target.value);
              if (errorClave) setErrorClave(null);
            }}
            error={errorClave}
          />
          {error ? (
            <Aviso tono="peligro" icono="error">
              <span role="alert">{error}</span>
            </Aviso>
          ) : null}
          <Boton type="submit" bloque tamano="lg" icono="key" cargando={ocupado} className="text-lg!">
            Guardar y entrar
          </Boton>
        </form>
      )}
      <p className="mt-8 text-center">
        <EnlacePrivacidad />
      </p>
    </Bienvenida>
  );
}
